import { MRT_ColumnDef, MRT_Row } from "material-react-table";
import { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  hasAllPermissions,
  hasPermission,
  useCheckPermissions,
  useTableFilter,
} from "@/shared";
import {
  useDeleteActivos,
  useUpdateActivos,
  usefetchActivos,
} from "@/shared/actions/erp";
import {
  CustomSearch,
  GeneralTable,
  SingleTableBoxScene,
  SwitchComponente,
} from "@/shared/components/ui";
import { Activos } from "@/shared/interfaces";
import { useUiConfirmModalStore } from "@/store/ui";
import { PermissionsEnum } from "@/auth/interfaces";

const returnUrlActivo = "/activos/activos";

export interface ActivosPageInterface {}

const ActivosPage: React.FC<ActivosPageInterface> = () => {
  useCheckPermissions(PermissionsEnum.api_view_activo);

  const navigate = useNavigate();
  ///* global state
  const setConfirmDialog = useUiConfirmModalStore((s) => s.setConfirmDialog);
  const setConfirmDialogIsOpen = useUiConfirmModalStore(
    (s) => s.setConfirmDialogIsOpen
  );
  const deleteActivosMutation = useDeleteActivos();

  ///* table
  const {
    globalFilter,
    pagination,
    searchTerm,
    onChangeFilter,
    setPagination,
    cleanFilter,
  } = useTableFilter();
  const { pageIndex } = pagination;
  ///* fetch dat
  const {
    data: ActivosPagingRes,
    isLoading,
    isRefetching,
  } = usefetchActivos({
    page: pageIndex + 1,
    codigo_barras: searchTerm,
  });
  ///* mutations
  ///* handlers
  const onEdit = (original: Activos) => {
    navigate(`${returnUrlActivo}/editar/${original.id_activo}`);
  };
  const onDelete = (original: Activos) => {
    setConfirmDialog({
      isOpen: true,
      subtitle: "¿Está seguro que desea eliminar el activo seleccionado?",
      title: "Confirmar",
      onConfirm: () => {
        deleteActivosMutation.mutate(original.id_activo!);
      },
      onClose() {
        setConfirmDialogIsOpen(false);
      },
    });
  };
  ///* columns
  const updateActivoMutation = useUpdateActivos({
    navigate,
    returnUrl: returnUrlActivo,
  });

  function switchComponente(row: MRT_Row<Activos>) {
    return (
      <SwitchComponente
        estadoSwitch={row.original.estado}
        titulo={row.original.estado ? "Activo" : "Inactivo"}
        setEstadoSwitch={(activo) => {
          if (!hasPermission(PermissionsEnum.api_change_activo)) return;
          setConfirmDialog({
            isOpen: true,
            title: "Cambiar Estado",
            subtitle:
              "¿Está seguro que desea cambiar el estado de este registro?",
            onConfirm: () => {
              setConfirmDialogIsOpen(false);
              updateActivoMutation.mutate({
                id: row.original.id_activo!,
                data: {
                  ...row.original,
                  estado: activo as boolean,
                },
              });
            },
          });
        }}
      />
    );
  }

  const columns = useMemo<MRT_ColumnDef<Activos>[]>(
    () => [
      {
        accessorKey: "numero_referencia",
        header: "Número de referencia",
        size: 200,
      },
      {
        accessorKey: "tipo_producto",
        header: "Tipo de producto",
        size: 200,
      },
      {
        accessorKey: "nombre",
        header: "Nombre",
        size: 200,
      },
      {
        accessorKey: "vida_util",
        header: "Vida útil",
        size: 200,
      },
      {
        accessorKey: "valor_residual",
        header: "Valor residual",
        size: 200,
      },
      {
        accessorKey: "anios_deprecacion",
        header: "Años depreciación",
        size: 200,
      },
      {
        accessorKey: "meses_deprecacion",
        header: "Meses depreciación",
        size: 200,
      },
      {
        accessorKey: "fecha_adquisicion",
        header: "Fecha adquisición",
        size: 200,
      },
      {
        accessorKey: "costo_adquisicion",
        header: "Costo adquisición",
        size: 200,
      },
      {
        accessorKey: "porcentaje_depreciacion",
        header: "Porcentaje depreciación",
        size: 200,
      },
      {
        accessorKey: "valor_deprecacion_anual",
        header: "Valor depreciación anual",
        size: 200,
      },
      {
        accessorKey: "valor_deprecacion_mensual",
        header: "Valor depreciación mensual",
        size: 200,
      },
      {
        accessorKey: "valor_deprecacion_diaria",
        header: "Valor depreciación diaria",
        size: 200,
      },
      {
        accessorKey: "observaciones",
        header: "Observaciones",
        size: 200,
      },
      {
        accessorKey: "estado",
        header: "Estado",
        size: 200,
        enableColumnFilter: false,
        enableSorting: false,
        Cell: ({ row }) => {
          return switchComponente(row);
        },
      },
    ],
    []
  );
  return (
    <SingleTableBoxScene
      title="Activos"
      urlRedirect={`${returnUrlActivo}/crear`}
      enableRedirect={hasPermission(PermissionsEnum.api_add_activo)}
    >
      <CustomSearch
        onChange={onChangeFilter}
        value={globalFilter}
        text="Activos"
        cleanFilter={cleanFilter}
      />

      <GeneralTable<Activos>
        columns={columns}
        data={ActivosPagingRes?.data?.data || []}
        //
        isLoading={isLoading}
        isRefetching={isRefetching}
        // // search
        enableGlobalFilter={false}
        // // pagination
        pagination={pagination}
        onPaging={setPagination}
        rowCount={ActivosPagingRes?.data?.count}
        // // actions
        enableColumnActions={hasAllPermissions([
          PermissionsEnum.api_change_activo,
        ])}
        // // actions
        actionsColumnSize={120}
        // crud
        onEdit={onEdit}
        canEdit={hasAllPermissions([PermissionsEnum.api_change_activo])}
        canDelete
        onDelete={onDelete}
      />
    </SingleTableBoxScene>
  );
};
export default ActivosPage;
